import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AttachmentIcon from "@mui/icons-material/Attachment";
import PropTypes from "prop-types";

const CertificatesDialog = ({
  open,
  onClose,
  selectedUserName,
  selectedUserCertificates,
  convertUTCToLocal,
  onFileClick,
  computeCertificateStatus,
  t,
  theme,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    sx={{
      "& .MuiDialog-paper": {
        boxShadow: theme.shadows[20],
      },
    }}
  >
    <DialogTitle
      sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AssignmentIcon />
        <Typography variant="h6" component="span" fontWeight="bold">
          {t("documentDetail.userCertificatesTitle", {
            name: selectedUserName,
          })}
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent sx={{ p: 3 }}>
      {selectedUserCertificates.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label={t("documentDetail.certificatesTableAria")}>
            <TableHead>
              <TableRow sx={{ bgcolor: "background.paper" }}>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("documentDetail.certificateTitle")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("documentDetail.certificateDescription")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("documentDetail.certificateCreatedAt")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("documentDetail.certificateExpires")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("documentDetail.certificateExpiresAt")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("documentDetail.certificateStatus")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("documentDetail.attachments")}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedUserCertificates.map((cert) => (
                <TableRow
                  key={cert.id}
                  sx={{ "&:hover": { bgcolor: "action.hover" } }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {cert.title || t("documentDetail.na")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cert.description || t("documentDetail.na")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {convertUTCToLocal(cert.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cert.expires
                        ? t("documentDetail.yes")
                        : t("documentDetail.no")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cert.expires
                        ? convertUTCToLocal(cert.expires_at)
                        : t("documentDetail.na")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = computeCertificateStatus(cert);
                      return (
                        <Chip
                          label={status.label}
                          color={status.severity}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            minWidth: 100,
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {cert.attachments.length > 0 ? (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 1,
                          maxWidth: 180,
                        }}
                      >
                        {cert.attachments.map((attachment) => (
                          <Box
                            key={attachment.id}
                            onClick={() => onFileClick(attachment.url)}
                            sx={{
                              cursor: "pointer",
                              borderRadius: 1,
                              overflow: "hidden",
                              border: "1px solid",
                              borderColor: "divider",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                borderColor: "primary.main",
                                transform: "scale(1.05)",
                              },
                            }}
                          >
                            {attachment.is_image ? (
                              <img
                                src={attachment.url}
                                alt={
                                  attachment.display_name ||
                                  t("documentDetail.attachmentAlt")
                                }
                                style={{
                                  objectFit: "cover",
                                  width: "100%",
                                  height: "50px",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  textAlign: "center",
                                  p: 1,
                                  height: "50px",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "action.hover",
                                }}
                              >
                                <AttachmentIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "text.secondary",
                                    mb: 0.25,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.65rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "100%",
                                  }}
                                >
                                  {attachment.display_name ||
                                    t("documentDetail.fileLabel")}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("documentDetail.none")}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <AssignmentIcon
            sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            {t("documentDetail.noCertificatesAvailable")}
          </Typography>
        </Box>
      )}
    </DialogContent>
    <DialogActions sx={{ p: 2, bgcolor: "background.paper" }}>
      <Button onClick={onClose} color="primary" variant="contained">
        {t("documentDetail.closeButton")}
      </Button>
    </DialogActions>
  </Dialog>
);

CertificatesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedUserName: PropTypes.string.isRequired,
  selectedUserCertificates: PropTypes.array.isRequired,
  convertUTCToLocal: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
  computeCertificateStatus: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

export default CertificatesDialog;
